import { getSessionToken, updateSessionToken, clearSessionToken } from '~/hooks/useAuth';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

// Generic API response type
export interface ApiResponse<T = any> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
    details?: any;
    token?: string;
    redirectUrl?: string;
    returnUrl?: string;
}

// Generic error class
export class ApiError extends Error {
    constructor(
        message: string,
        public status?: number,
        public code?: string
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

// Base API client class
class ApiClient {
    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        try {
            const token = getSessionToken();

            const response = await fetch(`${API_URL}/trpc/${endpoint}`, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                    ...options.headers,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));

                // Handle 401 Unauthorized - clear session and redirect to login
                if (response.status === 401) {
                    clearSessionToken();
                    if (typeof window !== 'undefined') {
                        window.location.href = '/join';
                    }
                }

                throw new ApiError(
                    errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`,
                    response.status,
                    errorData.code
                );
            }

            const data = await response.json();
            const result = data.result.data;

            // Handle token updates from response
            if (result.token) {
                updateSessionToken(result.token);
            }

            // Handle redirects from response
            if (checkForRedirects(result)) {
                // Redirect handled, don't return data
                throw new ApiError('Redirecting...', 302);
            }

            // If response indicates failure, throw error
            if (!result.success) {
                throw new ApiError(result.error || result.message || 'Unknown error');
            }

            return result.data;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(
                error instanceof Error ? error.message : 'Network error'
            );
        }
    }

    // GET request
    async get<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET' });
    }

    // POST request
    async post<T>(endpoint: string, data?: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    // PUT request
    async put<T>(endpoint: string, data?: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    // DELETE request
    async delete<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'DELETE' });
    }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Helper functions for redirect handling (from trpc-unwrapper-link functionality)
function handleRedirect(redirectUrl?: string, returnUrl?: string): boolean {
    if (!redirectUrl) return false;
    const url = `${redirectUrl}${returnUrl ? `?returnTo=${returnUrl}` : ''}`;
    if (/^https?:\/\//.test(url)) {
        if (typeof window !== 'undefined') {
            window.open(url, '_blank');
        }
        return true;
    } else {
        if (typeof window !== 'undefined') {
            window.location.href = url;
        }
        return true;
    }
}

function getCurrentPath(): string {
    if (typeof window !== 'undefined') {
        return window.location.pathname;
    } else {
        return '/';
    }
}

function checkForRedirects(response: ApiResponse<any>): boolean {
    if (!response) return false;

    const redirectUrl = response.redirectUrl;
    const returnUrl = response.returnUrl;
    const targetUrl = redirectUrl;

    if (!targetUrl) return false;

    // Don't redirect if we're already at the target path
    if (!targetUrl.startsWith('http')) {
        const currentPath = getCurrentPath();
        if (currentPath === targetUrl) {
            return false; // Already at target, no redirect needed
        }
    }

    if (handleRedirect(redirectUrl, returnUrl)) {
        return true;
    }

    return false;
}

// Helper function to check for pending redirects
export function getPendingRedirect(): string | null {
    if (typeof window === 'undefined') return null;
    const redirect = window.sessionStorage.getItem('pendingRedirect');
    if (redirect) {
        window.sessionStorage.removeItem('pendingRedirect');
        return redirect;
    }
    return null;
}

// Helper function to clear all session data
export function clearApiSession(): void {
    clearSessionToken();
    if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem('pendingRedirect');
    }
}
